import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Mail, MessageSquare, Copy } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Checkbox } from '@/components/ui/checkbox'
import type { MobilisationTeam } from '../../api'
import type { NotifyAllProgressEvent } from '../../hooks'
import { sanitizeRichText } from '@/lib/sanitize-html'

export type LastSendResultByTeam = Record<number, { sms?: { sent: number; failed?: number; errorMessage?: string }; email?: { sent: number; failed?: number; errorMessage?: string } }>

export type SendProgressByTeam = Record<number, { sms?: { sent: number; total: number }; email?: { sent: number; total: number } }>

interface Step2CommunicationProps {
    teams: MobilisationTeam[]
    onSendSms: (teamId: number) => void
    onSendEmail: (teamId: number) => void
    onSendToAll: () => void
    sending?: { teamId: number; channel: 'sms' | 'email' } | null
    sendingAll?: boolean
    sendAllProgress?: NotifyAllProgressEvent | null
    sendProgress?: SendProgressByTeam
    lastSendResult?: LastSendResultByTeam
}

type EditingCell = { teamId: number; field: 'sms' | 'email' }
type TeamSelection = { sms: boolean; email: boolean }

export function Step2Communication({
    teams,
    onSendSms,
    onSendEmail,
    onSendToAll,
    sending = null,
    sendingAll = false,
    sendAllProgress = null,
    sendProgress = {},
    lastSendResult = {},
}: Step2CommunicationProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [overrides, setOverrides] = useState<Record<number, { sms?: string; email?: string }>>({})
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null)
    const [editDraft, setEditDraft] = useState('')
    const [selections, setSelections] = useState<Record<number, TeamSelection>>({})
    const smsTextareaRef = useRef<HTMLTextAreaElement>(null)
    const emailEditorContainerRef = useRef<HTMLDivElement>(null)

    const getSelection = useCallback((teamId: number): TeamSelection => {
        return selections[teamId] ?? { sms: true, email: true }
    }, [selections])

    const setTeamSelection = useCallback((teamId: number, field: 'sms' | 'email', value: boolean) => {
        setSelections((prev) => ({
            ...prev,
            [teamId]: { ...(prev[teamId] ?? { sms: true, email: true }), [field]: value }
        }))
    }, [])

    const setAllSms = useCallback((checked: boolean) => {
        setSelections((prev) => {
            const next = { ...prev }
            teams.forEach((t) => {
                next[t.id] = { ...(next[t.id] ?? { sms: true, email: true }), sms: checked }
            })
            return next
        })
    }, [teams])

    const setAllEmail = useCallback((checked: boolean) => {
        setSelections((prev) => {
            const next = { ...prev }
            teams.forEach((t) => {
                next[t.id] = { ...(next[t.id] ?? { sms: true, email: true }), email: checked }
            })
            return next
        })
    }, [teams])

    const allSmsSelected = teams.length > 0 && teams.every((t) => getSelection(t.id).sms)
    const allEmailSelected = teams.length > 0 && teams.every((t) => getSelection(t.id).email)

    useEffect(() => {
        if (editingCell?.field === 'sms') {
            const focus = () => {
                const el = smsTextareaRef.current
                if (el) {
                    el.focus()
                    const len = el.value.length
                    el.setSelectionRange(len, len)
                }
            }
            const id = requestAnimationFrame(() => requestAnimationFrame(focus))
            return () => cancelAnimationFrame(id)
        }
    }, [editingCell])

    const getSms = useCallback((team: MobilisationTeam) => overrides[team.id]?.sms ?? team.smsMessage ?? '', [overrides])
    const getEmail = useCallback((team: MobilisationTeam) => overrides[team.id]?.email ?? team.emailMessage ?? '', [overrides])

    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            toast({ title: t('common.copiedToClipboard', 'Copied to clipboard.') })
        } catch {
            toast({ title: t('common.error', 'Error'), description: 'Failed to copy', variant: 'destructive' })
        }
    }

    const startEdit = (teamId: number, field: 'sms' | 'email', current: string) => {
        setEditingCell({ teamId, field })
        setEditDraft(current || '')
    }

    const saveEdit = () => {
        if (!editingCell) return
        const { teamId, field } = editingCell
        setOverrides((prev) => ({
            ...prev,
            [teamId]: { ...prev[teamId], [field]: editDraft }
        }))
        setEditingCell(null)
        setEditDraft('')
    }

    const cancelEdit = () => {
        setEditingCell(null)
        setEditDraft('')
    }

    const handleEmailBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        const next = e.relatedTarget as Node | null
        if (next && emailEditorContainerRef.current?.contains(next)) {
            return
        }
        saveEdit()
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold">{t('recoverRequests.mobilisation.communications', 'Communications')}</h1>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                        {t('recoverRequests.mobilisation.step2Description', 'Notify all recovery teams via SMS and email.')}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                    {sendingAll && sendAllProgress && (
                        <p className="text-sm text-muted-foreground tabular-nums" aria-live="polite">
                            {t('recoverRequests.mobilisation.teamProgress', 'Team {{current}} of {{total}}', {
                                current: sendAllProgress.teamIndex,
                                total: sendAllProgress.totalTeams,
                            })}
                            {sendAllProgress.teamName && ` – ${sendAllProgress.teamName}`}
                            {' · '}
                            {t('recoverRequests.mobilisation.sentSoFar', 'Sent')} {sendAllProgress.totalSentSoFar}
                            {sendAllProgress.totalFailedSoFar > 0 && (
                                <span className="text-destructive">
                                    {' · '}
                                    {t('recoverRequests.mobilisation.failedSoFar', 'Failed')} {sendAllProgress.totalFailedSoFar}
                                </span>
                            )}
                        </p>
                    )}
                    <Button
                        variant="default"
                        onClick={onSendToAll}
                        disabled={sendingAll || teams.length === 0}
                    >
                        {sendingAll
                            ? t('recoverRequests.mobilisation.sendingAll', 'Sending...')
                            : t('recoverRequests.mobilisation.sendToAll', 'Send to All')}
                    </Button>
                </div>
            </div>
            <div className="rounded-md border">
                <Table className="table-fixed">
                    <TableHeader>
                        <TableRow className="bg-table-header border-b border-table-border">
                            <TableHead className="font-semibold text-foreground w-[150px]">{t('recoverRequests.mobilisation.team', 'Team')}</TableHead>
                            <TableHead className="font-semibold text-foreground w-[30%]">{t('recoverRequests.mobilisation.smsMessage', 'SMS message')}</TableHead>
                            <TableHead className="font-semibold text-foreground w-[38%]">{t('recoverRequests.mobilisation.emailMessage', 'Email message')}</TableHead>
                            <TableHead className="font-semibold text-foreground w-[80px] text-center">
                                <label className="flex items-center justify-center gap-1.5 cursor-pointer">
                                    <Checkbox
                                        checked={allSmsSelected}
                                        onCheckedChange={(checked) => setAllSms(checked === true)}
                                    />
                                    <span className="text-sm whitespace-nowrap">{t('recoverRequests.mobilisation.sms', 'SMS')}</span>
                                </label>
                            </TableHead>
                            <TableHead className="font-semibold text-foreground w-[80px] text-center">
                                <label className="flex items-center justify-center gap-1.5 cursor-pointer">
                                    <Checkbox
                                        checked={allEmailSelected}
                                        onCheckedChange={(checked) => setAllEmail(checked === true)}
                                    />
                                    <span className="text-sm whitespace-nowrap">{t('recoverRequests.mobilisation.email', 'Email')}</span>
                                </label>
                            </TableHead>
                            <TableHead className="font-semibold text-foreground w-[140px] text-right">{t('recoverRequests.mobilisation.send', 'Send')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {teams.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                                    {t('recoverRequests.mobilisation.noTeams', 'No teams found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            teams.map((team) => {
                                const smsValue = getSms(team)
                                const emailValue = getEmail(team)
                                const isEditingSms = editingCell?.teamId === team.id && editingCell?.field === 'sms'
                                const isEditingEmail = editingCell?.teamId === team.id && editingCell?.field === 'email'
                                return (
                                    <TableRow key={team.id}>
                                        <TableCell className="font-medium w-[150px]">
                                            {team.teamName}
                                        </TableCell>
                                        <TableCell className="align-top py-2 w-[30%]">
                                            <div
                                                className="flex items-start gap-2 min-w-0 max-w-full"
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                {isEditingSms ? (
                                                    <Textarea
                                                        ref={smsTextareaRef}
                                                        value={editDraft}
                                                        onChange={(e) => setEditDraft(e.target.value)}
                                                        onBlur={saveEdit}
                                                        rows={4}
                                                        className="flex-1 min-w-0 h-[6.5rem] text-sm resize-none overflow-auto"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    />
                                                ) : (
                                                    <div
                                                        className="flex-1 min-w-0 h-[6.5rem] rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground whitespace-pre-wrap break-words cursor-text hover:border-ring transition-colors flex items-start overflow-auto"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            startEdit(team.id, 'sms', smsValue)
                                                        }}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    >
                                                        <span className="flex-1">{smsValue?.trim() || '–'}</span>
                                                    </div>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 shrink-0 mt-0.5"
                                                    onClick={(e) => { e.stopPropagation(); smsValue && handleCopy(smsValue) }}
                                                    disabled={!smsValue?.trim()}
                                                    title="Copy to clipboard"
                                                >
                                                    <Copy className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                        <TableCell className="align-top py-2 w-[38%]">
                                            <div
                                                className="flex items-start gap-2 min-w-0 max-w-full"
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                {isEditingEmail ? (
                                                    <div
                                                        ref={emailEditorContainerRef}
                                                        className="flex-1 min-w-0"
                                                        onBlur={handleEmailBlur}
                                                    >
                                                        <RichTextEditor
                                                            value={editDraft}
                                                            onChange={setEditDraft}
                                                            className="[&_.ProseMirror]:min-h-[100px]"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="flex-1 min-w-0 min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground prose prose-sm max-w-none dark:prose-invert cursor-text hover:border-ring transition-colors [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:[list-style-type:disc] [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:[list-style-type:decimal] [&_li]:my-0.5"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            startEdit(team.id, 'email', emailValue)
                                                        }}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(emailValue?.trim() || '–') }}
                                                    />
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 shrink-0 mt-0.5"
                                                    onClick={(e) => { e.stopPropagation(); emailValue && handleCopy(emailValue) }}
                                                    disabled={!emailValue?.trim()}
                                                    title="Copy to clipboard"
                                                >
                                                    <Copy className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                        <TableCell className="align-top py-2 w-[80px] text-center">
                                            <div className="flex justify-center">
                                                <Checkbox
                                                    checked={getSelection(team.id).sms}
                                                    onCheckedChange={(checked) => setTeamSelection(team.id, 'sms', checked === true)}
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell className="align-top py-2 w-[80px] text-center">
                                            <div className="flex justify-center">
                                                <Checkbox
                                                    checked={getSelection(team.id).email}
                                                    onCheckedChange={(checked) => setTeamSelection(team.id, 'email', checked === true)}
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell className="align-top py-2 w-[200px] text-right">
                                            <div className="flex flex-col items-end gap-1.5">
                                                {sendProgress[team.id]?.sms && sendProgress[team.id].sms!.total > 0 && (
                                                    <div className="w-full min-w-[100px] max-w-[180px]">
                                                        <div className="flex justify-between text-xs text-muted-foreground mb-0.5">
                                                            <span>{t('recoverRequests.mobilisation.sms', 'SMS')}</span>
                                                            <span className="tabular-nums">{sendProgress[team.id].sms!.sent}/{sendProgress[team.id].sms!.total}</span>
                                                        </div>
                                                        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                                            <div
                                                                className="h-full bg-primary rounded-full transition-all duration-300"
                                                                style={{ width: `${Math.min(100, (sendProgress[team.id].sms!.sent / sendProgress[team.id].sms!.total) * 100)}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {sendProgress[team.id]?.email && sendProgress[team.id].email!.total > 0 && (
                                                    <div className="w-full min-w-[100px] max-w-[180px]">
                                                        <div className="flex justify-between text-xs text-muted-foreground mb-0.5">
                                                            <span>{t('recoverRequests.mobilisation.email', 'Email')}</span>
                                                            <span className="tabular-nums">{sendProgress[team.id].email!.sent}/{sendProgress[team.id].email!.total}</span>
                                                        </div>
                                                        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                                            <div
                                                                className="h-full bg-primary rounded-full transition-all duration-300"
                                                                style={{ width: `${Math.min(100, (sendProgress[team.id].email!.sent / sendProgress[team.id].email!.total) * 100)}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {sending != null && sending.teamId === team.id && !sendProgress[team.id]?.[sending.channel] && (
                                                    <>
                                                        <div className="h-1.5 w-full min-w-[80px] max-w-[120px] rounded-full bg-muted overflow-hidden">
                                                            <div
                                                                className="h-full w-1/3 rounded-full bg-primary animate-[send-progress_1.2s_ease-in-out_infinite]"
                                                            />
                                                        </div>
                                                        <span className="text-xs text-muted-foreground">
                                                            {t('recoverRequests.mobilisation.sending', 'Sending…')}
                                                        </span>
                                                    </>
                                                )}
                                                {lastSendResult[team.id] && (sending == null || sending.teamId !== team.id) && !sendProgress[team.id] && (
                                                    <div className="text-xs text-muted-foreground space-y-0.5 text-right max-w-[220px]">
                                                        {lastSendResult[team.id].sms != null && (
                                                            <div>
                                                                SMS: {lastSendResult[team.id].sms!.sent} {t('recoverRequests.mobilisation.sent', 'sent')}
                                                                {lastSendResult[team.id].sms!.failed != null && lastSendResult[team.id].sms!.failed! > 0 && (
                                                                    <span className="text-destructive">, {lastSendResult[team.id].sms!.failed} failed</span>
                                                                )}
                                                                {lastSendResult[team.id].sms!.errorMessage && (
                                                                    <div className="text-destructive mt-0.5 text-[11px] break-words" title={lastSendResult[team.id].sms!.errorMessage}>
                                                                        {lastSendResult[team.id].sms!.errorMessage}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {lastSendResult[team.id].email != null && (
                                                            <div>
                                                                Email: {lastSendResult[team.id].email!.sent} {t('recoverRequests.mobilisation.sent', 'sent')}
                                                                {lastSendResult[team.id].email!.failed != null && lastSendResult[team.id].email!.failed! > 0 && (
                                                                    <span className="text-destructive">, {lastSendResult[team.id].email!.failed} failed</span>
                                                                )}
                                                                {lastSendResult[team.id].email!.errorMessage && (
                                                                    <div className="text-destructive mt-0.5 text-[11px] break-words" title={lastSendResult[team.id].email!.errorMessage}>
                                                                        {lastSendResult[team.id].email!.errorMessage}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="shrink-0"
                                                    onClick={() => {
                                                        const sel = getSelection(team.id)
                                                        if (sel.sms) onSendSms(team.id)
                                                        if (sel.email) onSendEmail(team.id)
                                                    }}
                                                    disabled={sendingAll || (sending != null && sending.teamId === team.id) || (sendProgress[team.id] && (sendProgress[team.id].sms || sendProgress[team.id].email)) || (!getSelection(team.id).sms && !getSelection(team.id).email)}
                                                >
                                                    {t('recoverRequests.mobilisation.send', 'Send')}
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
